import { useState, useRef } from 'react';
import { useAdaptivePoll } from '@shared/hooks/useAdaptivePoll';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Clock } from 'lucide-react';
import { getToken } from '@shared/services/api';
import { fetchShared, SHARED_KEYS } from '@shared/services/sharedResource';

interface TrialStatus {
  isTrialing: boolean;
  daysRemaining: number;
  hoursRemaining: number;
}

export default function TrialBadge() {
  const { t } = useTranslation('billing');
  const [trialStatus, setTrialStatus] = useState<TrialStatus | null>(null);
  const [loading, setLoading] = useState(true);

  // Read inside the adaptive poll's fetcher so the poll is not recreated on
  // every response.
  const trialStatusRef = useRef<string>('');
  const fetchTrialStatusRef = useRef<() => Promise<void>>(async () => {});

  const fetchTrialStatus = async () => {
    try {
      const token = getToken();
      if (!token) return;

      // Shared with SubscriptionBanner — one request per page load instead of
      // one per component.
      const data = await fetchShared<{
        subscription?: { status?: string; trialEnd?: string };
      }>(SHARED_KEYS.subscription, async () => {
        const response = await fetch('/api/v1/billing/subscription', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error('Failed to fetch subscription');
        return response.json();
      });

      if (
        data.subscription &&
        data.subscription.status === 'TRIALING' &&
        data.subscription.trialEnd
      ) {
        const trialEnd = new Date(data.subscription.trialEnd);
        const now = new Date();
        const diffTime = trialEnd.getTime() - now.getTime();
        const diffDays =
          diffTime < 1000 * 60 * 60 * 24 ? 0 : Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        const diffHours = Math.ceil(diffTime / (1000 * 60 * 60));

        setTrialStatus({
          isTrialing: true,
          daysRemaining: Math.max(0, diffDays),
          hoursRemaining: Math.max(0, diffHours),
        });
      }
    } catch (error) {
      console.error('Error fetching trial status:', error);
    } finally {
      setLoading(false);
    }
  };

  trialStatusRef.current = trialStatus
    ? `${trialStatus.isTrialing}:${trialStatus.daysRemaining}:${trialStatus.hoursRemaining}`
    : '';
  fetchTrialStatusRef.current = fetchTrialStatus;

  // Keep the countdown accurate without polling background tabs. Adaptive:
  // 1h base, stretching to 6h once the remaining time stops changing.
  useAdaptivePoll({
    enabled: true,
    baseIntervalMs: 60 * 60 * 1000,
    maxIntervalMs: 6 * 60 * 60 * 1000,
    fetcher: async () => {
      await fetchTrialStatusRef.current();
      return trialStatusRef.current;
    },
    isEqual: (a, b) => a === b,
  });

  if (loading || !trialStatus || !trialStatus.isTrialing) {
    return null;
  }

  const { daysRemaining, hoursRemaining } = trialStatus;

  // Urgency maps onto the shared state tones: ok -> warn (<=3 days) -> danger (<=1 day).
  let badgeClass =
    'bg-state-ok-bg-light text-state-ok-light border-state-ok-light/20 dark:bg-state-ok-bg-dark dark:text-state-ok-dark dark:border-state-ok-dark/20';
  if (daysRemaining <= 1) {
    badgeClass =
      'bg-state-danger-bg-light text-state-danger-light border-state-danger-light/20 dark:bg-state-danger-bg-dark dark:text-state-danger-dark dark:border-state-danger-dark/20 animate-pulse';
  } else if (daysRemaining <= 3) {
    badgeClass =
      'bg-state-warn-bg-light text-state-warn-light border-state-warn-light/20 dark:bg-state-warn-bg-dark dark:text-state-warn-dark dark:border-state-warn-dark/20';
  }

  const timeText =
    daysRemaining > 0
      ? t('trial.daysLeft', { count: daysRemaining })
      : t('trial.hoursLeft', { count: hoursRemaining });

  return (
    <Link
      to="/billing"
      className={`flex min-h-11 items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-bold transition-all hover:scale-105 ${badgeClass}`}
      title={t('trial.tooltip')}
    >
      <Clock size={14} />
      <span>{t('trial.label', { time: timeText })}</span>
    </Link>
  );
}
